import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Button,
  Divider,
  EmptyState,
  StaticSelect,
  Skeleton,
  showToast,
  TextInput,
  ConfirmModal,
  CustomTooltip,
} from "../../../../components/shared";
import { ProjectsIcon, ArrowHead, SearchIcon, CloseIcon } from "../../../../assets/icons";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { HiOutlineUserPlus } from "react-icons/hi2";
import { useProjectDetails } from "../../../../lib/react-query/tasks";
import {
  useCancelDashboardInvite,
  useProjectMembers,
  useRemoveMember,
} from "../../../../lib/react-query/projects";
import DeleteProjectModal from "../../../../components/Projects/DeleteProjectModal";
import EditProjectModal from "../../../../components/Projects/EditProjectModal";
import InviteMemberModal from "../../../../components/Projects/InviteMemberModal";
import ProjectMemberCard from "../../../../components/Projects/ProjectMemberCard";
import EditProjectMemberModal from "../../../../components/Projects/EditProjectMemberModal";
import { PROJECT_ROLE_OPTIONS } from "../../../../constants/roles";
import { getFullName, toUsDate } from "../../../../utils/utils";
import { useIsClient } from "../../../../hooks/useClientRole";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const ProjectSettings = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const numericId = Number(id);
  const isValidProjectId = Number.isFinite(numericId);
  const { isClient: isClientModeActive } = useIsClient();

  // Filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const searchDebounceRef = useRef();

  // Modal state
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [removeMemberData, setRemoveMemberData] = useState(null);
  const [cancelMemberData, setCancelMemberData] = useState(null);

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const trimmed = (searchQuery || "").trim();
    if (trimmed.length >= 1) {
      searchDebounceRef.current = setTimeout(() => {
        setDebouncedSearch(trimmed);
      }, 250);
    } else {
      setDebouncedSearch("");
    }
  }, [searchQuery]);

  // Queries
  const { data: project, isLoading: projectLoading } = useProjectDetails(
    isValidProjectId ? numericId : null,
    isValidProjectId
  );
  const { data: membersData, isLoading: membersLoading } = useProjectMembers(
    isValidProjectId ? numericId : null,
    { search: debouncedSearch, role: roleFilter },
    isValidProjectId
  );
  const { mutateAsync: removeMember, isPending: removingMember } = useRemoveMember();
  const { mutateAsync: cancelInvitation, isPending: cancellingInvitation } = useCancelDashboardInvite();

  const memberGradient = project?.header_gradient || "gradient-4";

  const handleEditMember = (member) => {
    setEditMember(member);
  };

  const handleRemoveMember = (member) => {
    const name = member?.user?.full_name || member?.user?.name || getFullName(member?.user) || "this member";
    setRemoveMemberData({ ...member, _displayName: name });
  };

  const handleCancelInvitation = (member) => {
    const name = member?.user?.full_name || member?.user?.name || getFullName(member?.user) || "this member";
    setCancelMemberData({ ...member, _displayName: name });
  };

  const confirmCancelInvitation = async () => {
    if (!cancelMemberData) return;
    try {
      await cancelInvitation({ memberId: cancelMemberData.id, projectId: numericId });
      showToast("Invitation cancelled", "success", { position: "top-center" });
      setCancelMemberData(null);
    } catch (err) {
      showToast("Failed to cancel invitation", "error", { position: "top-center" });
    }
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberData) return;
    try {
      await removeMember({
        projectId: numericId,
        userId: removeMemberData?.user?.id,
      });
      showToast("Member removed", "success", { position: "top-center" });
      setRemoveMemberData(null);
    } catch (err) {
      showToast("Failed to remove member", "error", { position: "top-center" });
    }
  };

  const members = membersData?.items || membersData || [];
  const memberCount = members.length;
  const canCancelInvitation = ["OWNER", "MANAGER"].includes(project?.user_role);

  return (
    <div>
      <div className="bg-bggrey pt-4 sticky top-0 z-50 rounded-b-xl">
        <div className="bg-white rounded-xl">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0 px-4 py-3">
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto">
              <ProjectsIcon width={30} height={30} className="text-black flex-shrink-0" />
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="text-sm text-grey hover:text-secondary-dark transition"
                  onClick={() => navigate("/projects")}
                >
                  Projects
                </button>
                <span className="text-grey">/</span>
                <CustomTooltip content={project?.title || "—"}>
                  <span className="text-sm font-semibold max-w-[150px] truncate inline-block align-bottom">{project?.title || "—"}</span>
                </CustomTooltip>
                <span className="text-grey">/</span>
                <span className="text-sm">Settings</span>
              </div>
            </div>
            <div className="font-sans flex items-center gap-4 w-full sm:w-auto justify-end">
              <button
                type="button"
                className="flex items-center gap-1 rounded-md hover:bg-lightgrey text-sm px-2 py-1"
                onClick={() => navigate(`/projects/${numericId}/tasks`)}
                aria-label="Back"
                title="Back"
              >
                <ArrowHead width={16} height={16} className="text-darkgrey" />
                <span className="hidden sm:inline">Back</span>
              </button>
              {!isClientModeActive && (
                <>
                  <Button
                    className="rounded-lg"
                    onClick={() => setOpenEditModal(true)}
                    variant="outlined"
                    color="secondary"
                    size="md"
                  >
                    Edit Project
                  </Button>
                  <Button
                    className="rounded-lg"
                    onClick={() => setOpenDeleteModal(true)}
                    variant="outlined"
                    color="danger"
                    size="md"
                  >
                    Delete Project
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="px-2">
            <Divider />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl p-6">
        {projectLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="w-full h-16 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-4">Project Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <p className="text-xs text-grey mb-1">Title</p>
                  <p className="text-sm font-medium">{project?.title || "—"}</p>
                </div>
                <div className="border rounded-lg p-4">
                  <p className="text-xs text-grey mb-1">Status</p>
                  <p className="text-sm font-medium">{project?.status || "—"}</p>
                </div>
                <div className="border rounded-lg p-4">
                  <p className="text-xs text-grey mb-1">Start Date</p>
                  <p className="text-sm font-medium">
                    {project?.start_date
                      ? toUsDate(project.start_date)
                      : "—"}
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <p className="text-xs text-grey mb-1">End Date</p>
                  <p className="text-sm font-medium">
                    {project?.end_date
                      ? toUsDate(project.end_date)
                      : "—"}
                  </p>
                </div>
              </div>
            </div>

            {project?.description && (
              <div className="border rounded-lg p-4">
                <p className="text-xs text-grey mb-1">Description</p>
                <p className="text-sm">{project.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Members Section */}
      <div className="mt-6">
        <div className="bg-white rounded-xl border border-surface-border shadow-sm overflow-hidden">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 px-6 py-4 border-b border-surface-border">
            <div>
              <h3 className="text-lg font-sans font-semibold text-darkgrey">Members</h3>
              <p className="text-sm text-grey">
                {membersLoading ? "Loading members…" : `${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
              </p>
            </div>
            {!isClientModeActive && (
              <Button
                onClick={() => setOpenInviteModal(true)}
                variant="outlined"
                color="secondary"
                size="md"
                icon={HiOutlineUserPlus}
                className="gap-2"
              >
                Invite Member
              </Button>
            )}
          </div>

          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row gap-3 px-6 py-4 bg-lightgrey/50 border-b border-surface-border">
            <div className="flex-1">
              <TextInput
                label=""
                type="text"
                name="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                startContent={
                  <SearchIcon width={16} height={16} className="text-darkgrey" />
                }
                endContent={
                  searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="hover:bg-lightgrey rounded-full p-0.5 transition-colors"
                    >
                      <CloseIcon width={14} height={14} className="text-darkgrey" />
                    </button>
                  ) : null
                }
              />
            </div>
            <div className="w-full sm:w-44">
              <StaticSelect
                options={[
                  { id: "", name: "All Roles" },
                  ...PROJECT_ROLE_OPTIONS,
                ]}
                label=""
                name="roleFilter"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                placeholder="All Roles"
              />
            </div>
          </div>

          {/* Members Grid */}
          <div className="p-6">
            {membersLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="w-full h-56 rounded-2xl" />
                ))}
              </div>
            ) : members.length > 0 ? (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {members.map((member) => (
                  <ProjectMemberCard
                    key={member?.user?.id || member?.user_id || member?.id}
                    member={member}
                    gradient={memberGradient}
                    onEdit={handleEditMember}
                    onRemove={handleRemoveMember}
                    onCancelInvitation={handleCancelInvitation}
                    canCancelInvitation={canCancelInvitation}
                    isCancelling={cancellingInvitation}
                    variants={itemVariants}
                  />
                ))}
              </motion.div>
            ) : (
              <EmptyState
                compact
                icon={HiOutlineUserGroup}
                title="No members found"
                description={searchQuery || roleFilter ? "Try adjusting your search or filter." : "Invite members to join your project."}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {!isClientModeActive && (
        <>
          <EditProjectModal
            isOpen={openEditModal}
            onClose={() => setOpenEditModal(false)}
            projectId={numericId}
            initialProject={project}
          />
          <DeleteProjectModal
            isOpen={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            projectId={numericId}
            projectTitle={project?.title}
            onDeleted={() => navigate("/projects")}
          />
          <InviteMemberModal
            isOpen={openInviteModal}
            onClose={() => setOpenInviteModal(false)}
            projectId={numericId}
          />
          <EditProjectMemberModal
            isOpen={!!editMember}
            onClose={() => setEditMember(null)}
            member={editMember}
            projectId={numericId}
          />
        </>
      )}

      {/* Remove Member Confirmation */}
      <ConfirmModal
        isOpen={!!removeMemberData}
        onClose={() => setRemoveMemberData(null)}
        onConfirm={confirmRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove ${removeMemberData?._displayName || 'this member'} from the project?`}
        confirmText="Remove"
        isLoading={removingMember}
      />

      <ConfirmModal
        isOpen={!!cancelMemberData}
        onClose={() => setCancelMemberData(null)}
        onConfirm={confirmCancelInvitation}
        title="Cancel expired invitation"
        message={`Cancel the expired invitation for ${cancelMemberData?._displayName || "this member"}?`}
        confirmText="Cancel invitation"
        confirmColor="danger"
        isLoading={cancellingInvitation}
      />
    </div>
  );
};

export default ProjectSettings;
